import { useEffect, useState, type ReactNode } from 'react'
import { Lock, Clock, X } from 'lucide-react'
import { apiGet, adminMutate, Modal, type ToastFn } from '../admin/primitives.js'
import type { ConfirmOptions } from '../shell/confirm.js'

/**
 * ACCESS OPERABILITY V1 — socle partagé du module Contrôle d'accès :
 *  · UNE source d'état des actions (`useAccessCaps`, resolver V2 côté serveur,
 *    jamais recalculé au frontend, invalidable sans reload §10) ;
 *  · traduction HUMAINE des énums provider (§64) — le brut reste en diagnostic ;
 *  · `ActionButton` : un bouton d'écriture est UTILISABLE ou EXPLIQUÉ (§8-9) ;
 *  · `runCommand` : POST commande → attente du résultat provider réel (§113).
 */

/* ─────────── capacités : cache module + abonnés + invalidation ─────────── */

export interface ActionInfo { action: string; permission: string; status: string; reason: string | null; critical: boolean }
export interface Caps {
  mode: { configured: string; effective: string; global: string; max: string; limitingFactor: string | null; canRead: boolean; canWrite: boolean; effectiveLabel: string }
  actions: ActionInfo[]
  operability: { operable: number; total: number; deferred: number }
}

let capsCache: Caps | null = null
let capsInflight: Promise<Caps> | null = null
const capsSubs = new Set<(c: Caps) => void>()

function loadCaps(): Promise<Caps> {
  if (capsCache) return Promise.resolve(capsCache)
  if (capsInflight) return capsInflight
  capsInflight = apiGet<Caps>('/api/v1/access/capabilities')
    .then((c) => { capsCache = c; capsSubs.forEach((f) => f(c)); return c })
    .finally(() => { capsInflight = null })
  return capsInflight
}

/** À appeler après un changement de Mode sûr : refetch, sans reload (§10). */
export function invalidateAccessCaps(): void {
  capsCache = null
  void loadCaps()
}

/**
 * `actif` (vrai par défaut) : le menu clic droit ne lit les capacités que pour
 * un compte qui pourrait déverrouiller — pas de lecture, ni de refus, pour rien.
 */
export function useAccessCaps(actif = true): Caps | null {
  const [caps, setCaps] = useState<Caps | null>(capsCache)
  useEffect(() => {
    if (!actif) return
    const f = (c: Caps) => setCaps(c)
    capsSubs.add(f)
    void loadCaps().then(f).catch(() => {})
    return () => { capsSubs.delete(f) }
  }, [actif])
  return caps
}

export function actionInfo(caps: Caps | null, action: string): ActionInfo | null {
  return caps?.actions.find((a) => a.action === action) ?? null
}

/* ─────────── traductions provider → humain (§64) ─────────── */

export const MODE_LABELS: Record<string, string> = { DISABLED: 'Désactivée', READ_ONLY: 'Lecture seule', EDIT: 'Édition', AUTOMATION: 'Automatisations' }
export const PERSON_STATUS: Record<string, string> = { ACTIVE: 'Active', DEACTIVATED: 'Désactivée', PENDING: 'En attente', UNKNOWN: 'Inconnue' }
export const CRED_STATUS: Record<string, string> = { ASSIGNED: 'Assignée', UNASSIGNED: 'Non assignée', ACTIVE: 'Active', INACTIVE: 'Inactive', SUSPENDED: 'Suspendue', PENDING: 'En attente', DELETED: 'Supprimée', DISABLED: 'Désactivée', ACTIVATED: 'Activée' }
export const CRED_TYPE: Record<string, string> = { id_card: 'Carte NFC', ua_card: 'Carte UA', touch_pass: 'Touch Pass', pin: 'PIN', qr: 'QR' }
export const RESULT_LABELS: Record<string, string> = { ACCESS: 'Accordé', BLOCKED: 'Refusé', SUCCESS: 'Accordé', FAILURE: 'Échec', FAILED: 'Échec' }
export const METHOD_LABELS: Record<string, string> = {
  FACE: 'Visage', NFC: 'NFC', PIN_CODE: 'PIN', WALLET_NFC_IPHONE: 'iPhone', WALLET_NFC_IWATCH: 'Watch',
  TOUCH_PASS: 'Touch Pass', QR_CODE: 'QR', LICENSE_PLATE: 'Plaque', CALL: 'Interphone', REX: 'Bouton sortie',
  // Actions distantes (§210) : jamais présentées comme un credential humain.
  REMOTE_THROUGH_UAH: 'Ouverture distante', REMOTE: 'Ouverture distante', REMOTE_UNLOCK: 'Ouverture distante',
}
export const TP_SOURCE: Record<string, string> = { apple: 'Apple Wallet', google: 'Google Wallet' }

// Le provider mélange les casses (status `assigned` vs `ASSIGNED`) : on
// traduit indépendamment de la casse, le brut reste en diagnostic.
export const tr = (map: Record<string, string>, v: string | null | undefined): string => (v ? (map[v] ?? map[v.toUpperCase()] ?? v) : '—')

export function personKindLabel(p: { personId?: string | null; kind?: string }): string {
  if (p.kind === 'VISITOR') return 'Visiteur'
  return p.personId ? 'Employé lié' : 'Personne Access'
}

/**
 * AVATAR PERSONNE (V2.1 §163-169) — chaîne de priorité UNIQUE : photo Gestion
 * → photo Access (miroir officiel /system/static) → initiales. `photoUrl` est
 * résolue PAR LE SERVEUR (photoSets) ; le frontend ne devine rien.
 */
export function PersonAvatar({ name, photoUrl, visitor, size }: { name: string | null | undefined; photoUrl?: string | null | undefined; visitor?: boolean; size?: 'lg' }) {
  const cls = `ac-avatar ${visitor ? 'ac-avatar--visitor' : ''} ${size === 'lg' ? 'ac-avatar--lg' : ''}`
  const [broken, setBroken] = useState(false)
  if (photoUrl && !broken) return <img className={`${cls} ac-avatar--photo`} src={photoUrl} alt="" loading="lazy" onError={() => setBroken(true)} />
  return <span className={cls}>{initials(name)}</span>
}

export function initials(name: string | null | undefined): string {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : '')).toUpperCase() || '??'
}
export const timeShort = (at: string | null): string => (at ? new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—')
/**
 * HEURE HONNÊTE (V2.1 §136-140) : « 07:24 » seul laissait croire qu'un
 * événement d'avant-hier venait d'arriver — LA cause du faux bug « refusés
 * incohérents ». Le jour est TOUJOURS dit dès que ce n'est pas aujourd'hui.
 * Fuseau : Europe/Paris (celui du navigateur du club).
 */
export function dayTime(at: string | null): string {
  if (!at) return '—'
  const d = new Date(at)
  const t = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const today = new Date(); today.setHours(0, 0, 0, 0)
  if (d.getTime() >= today.getTime()) return t
  if (d.getTime() >= today.getTime() - 86_400_000) return `hier ${t}`
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${t}`
}
export const dateTime = (at: string | null): string => (at ? new Date(at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—')
export function relativeDay(at: string | null): string {
  if (!at) return '—'
  const d = new Date(at)
  // Jours CALENDAIRES, pas tranches de 24 h : hier 15:36 doit dire « hier »,
  // jamais « 15:36 » nu (même mensonge que dayTime corrige, §136-140).
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const dmid = new Date(d); dmid.setHours(0, 0, 0, 0)
  const days = Math.round((today.getTime() - dmid.getTime()) / 86_400_000)
  if (days <= 0) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (days === 1) return 'hier'
  if (days < 7) return `il y a ${days} j`
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}

/* ─────────── hook API générique du module ─────────── */

export function useApi<T>(url: string): { data: T | null; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [n, setN] = useState(0)
  useEffect(() => {
    let alive = true
    void apiGet<T & { error?: string; message?: string }>(url).then((j) => {
      if (!alive) return
      if (j && (j as { error?: string }).error) setError((j as { message?: string }).message ?? 'Accès refusé.')
      else { setError(null); setData(j as T) }
    }).catch(() => alive && setError('Gestion n’a pas répondu.'))
    return () => { alive = false }
  }, [url, n])
  return { data, error, reload: () => setN((x) => x + 1) }
}

/* ─────────── exécution d'une commande provider (§113-115) ─────────── */

export interface CommandOutcome { ok: boolean; error: string | null; result?: unknown }

/** POST la commande puis ATTEND le verdict provider réel — jamais d'optimisme. */
export async function runCommand(action: string, payload: Record<string, unknown>, opts: { timeoutMs?: number } = {}): Promise<CommandOutcome> {
  const r = await adminMutate<{ commandId?: string; error?: string; message?: string }>('POST', '/api/v1/access/commands', { action, payload })
  if (r.status >= 400 || !r.data.commandId) return { ok: false, error: r.data.message ?? r.data.error ?? 'Commande refusée.' }
  const deadline = Date.now() + (opts.timeoutMs ?? 90_000)
  while (Date.now() < deadline) {
    await new Promise((res) => setTimeout(res, 1200))
    const st = await apiGet<{ status: string; error: string | null; result?: unknown }>(`/api/v1/access/commands/${r.data.commandId}`)
    if (st.status === 'SUCCEEDED') return { ok: true, error: null, result: st.result }
    if (st.status === 'FAILED' || st.status === 'CONFLICT' || st.status.startsWith('BLOCKED') || st.status === 'PROVIDER_UNSUPPORTED') {
      return { ok: false, error: st.error ?? 'La commande a été refusée à l’exécution.' }
    }
  }
  return { ok: false, error: 'UniFi Access n’a pas confirmé dans le temps imparti — vérifier l’historique de commandes.' }
}

/* ─────────── DÉVERROUILLER une porte — UNE implémentation (fiche + menu) ─────────── */

/** Ce que la confirmation montre de la porte : relu à chaque demande. */
interface PorteLue { id: string; name: string; alias: string | null; providerName: string | null; modeLabel: string; lock: string | null; position: 'OPEN' | 'CLOSED' | 'NOT_REPORTED' }

/** Une porte, une commande à la fois : le menu ne double jamais le bouton de la fiche. */
const deverrouillagesEnCours = new Set<string>()

/**
 * Le geste « Déverrouiller » de la fiche porte, partagé avec le menu clic
 * droit (24/10/2026, règle d'Hugo : une seule implémentation). La porte est
 * RELUE (GET /doors/:id, access.doors.view) pour que la confirmation dise son
 * état réel, même depuis une liste qui n'a pas tous ses champs. Ensuite : la
 * même confirmation critique, la même commande ACCESS_DOOR_UNLOCK — droit
 * access.doors.unlock, périmètre de la porte et mode sûr tranchés par le
 * serveur — et le même toast. Vrai quand UniFi Access a confirmé.
 *
 * `enCours` : la commande part (après le oui), puis a répondu.
 */
export async function deverrouillerPorte(id: string, outils: {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  toast: ToastFn
  enCours?: ((oui: boolean) => void) | undefined
}): Promise<boolean> {
  const { confirm, toast, enCours } = outils
  if (deverrouillagesEnCours.has(id)) {
    toast('error', 'Un déverrouillage de cette porte est déjà en cours : attendez la réponse d’UniFi Access.')
    return false
  }
  deverrouillagesEnCours.add(id)
  let parti = false
  try {
    let data: PorteLue
    try {
      const j = await apiGet<PorteLue & { error?: string; message?: string }>(`/api/v1/access/doors/${encodeURIComponent(id)}`)
      if (j.error) { toast('error', j.message ?? (j.error === 'NOT_FOUND' ? 'Porte introuvable dans Gestion.' : 'Accès refusé.')); return false }
      data = j
    } catch {
      toast('error', 'Gestion n’a pas répondu.')
      return false
    }
    if (!(await confirm({
      title: `Déverrouiller ${data.name} ?`,
      message: 'La serrure sera réellement actionnée immédiatement.',
      details: [
        { label: 'Porte', value: `${data.name}${data.alias ? ` (${data.providerName})` : ''}` },
        { label: 'Règle actuelle', value: data.modeLabel },
        { label: 'Verrou', value: data.lock === 'lock' ? 'Verrouillée' : 'Déverrouillée' },
        { label: 'Position', value: data.position === 'OPEN' ? 'Ouverte' : data.position === 'CLOSED' ? 'Fermée' : 'Non remontée' },
      ],
      variant: 'critical', confirmLabel: 'Déverrouiller',
    }))) return false
    parti = true
    enCours?.(true)
    let r: CommandOutcome
    try {
      r = await runCommand('ACCESS_DOOR_UNLOCK', { door_id: data.id })
    } catch {
      // La commande a pu partir : on ne la relance pas à l'aveugle.
      toast('error', 'Gestion n’a plus répondu pendant la commande — vérifier l’historique de commandes avant de réessayer.')
      return false
    }
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return false }
    toast('ok', `Porte « ${data.name} » déverrouillée par UniFi Access.`)
    return true
  } finally {
    deverrouillagesEnCours.delete(id)
    if (parti) enCours?.(false)
  }
}

/* ─────────── entête de colonne TRIABLE (§45-46) ─────────── */

/**
 * NOM ↕ → clic → NOM ↑ → re-clic → NOM ↓. Chaque colonne déclare sa paire
 * (asc, desc) ; le tri actif affiche un chevron animé (Motion V2 : micro).
 */
export function SortHeader(props: { label: string; asc: string; desc: string; sort: string; onSort: (s: string) => void }) {
  const state = props.sort === props.asc ? 'asc' : props.sort === props.desc ? 'desc' : 'none'
  return (
    <button
      type="button"
      className={`ac-sorth ${state !== 'none' ? 'is-on' : ''}`}
      aria-sort={state === 'asc' ? 'ascending' : state === 'desc' ? 'descending' : 'none'}
      onClick={() => props.onSort(state === 'asc' ? props.desc : props.asc)}
    >
      {props.label}
      <span className={`ac-sorth__chev ac-sorth__chev--${state}`} aria-hidden="true">{state === 'desc' ? '↓' : state === 'asc' ? '↑' : '↕'}</span>
    </button>
  )
}

/* ─────────── modale du module (compose la Modal canonique) ─────────── */

export function AccessModal(props: { title: string; onClose: () => void; footer?: ReactNode; width?: number; children: ReactNode }) {
  return (
    <Modal open onClose={props.onClose} ariaLabel={props.title} width={props.width ?? 640}>
      <header className="lc-modal__head">
        <div><div className="lc-modal__title">{props.title}</div></div>
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={props.onClose} aria-label="Fermer"><X size={18} /></button>
      </header>
      <div className="lc-modal__body">
        {props.children}
        {props.footer ? <div className="ac-modal-foot">{props.footer}</div> : null}
      </div>
    </Modal>
  )
}

/* ─────────── ActionButton : utilisable OU expliqué (§8-9) ─────────── */

const BLOCK_ICON: Record<string, string> = {
  BLOCKED_SAFE_MODE: '🔒', BLOCKED_PERMISSION: '🔒', BLOCKED_SCOPE: '🔒',
  ACTION_DEFERRED: '—', PROVIDER_UNSUPPORTED: '—',
}

export function ActionButton(props: {
  caps: Caps | null
  action: string
  label: ReactNode
  onRun: () => void
  primary?: boolean
  busy?: boolean
  icon?: ReactNode
  /** Raison supplémentaire côté page (ex. porte hors périmètre). */
  extraBlock?: string | null
}) {
  const info = actionInfo(props.caps, props.action)
  // La permission est déjà résolue PAR LE SERVEUR dans /access/capabilities —
  // le frontend ne recalcule rien (§5).
  const blockedReason = props.extraBlock ?? (info && info.status !== 'AVAILABLE' ? info.reason : null)
  const loading = !props.caps
  if (loading) return <button type="button" className="lc-btn" disabled>{props.label}</button>
  if (blockedReason) {
    return (
      <span className="ac-blocked" title={blockedReason}>
        <button type="button" className="lc-btn ac-blocked__btn" disabled aria-disabled="true">
          <Lock size={13} aria-hidden="true" /> {props.label}
        </button>
        <span className="ac-blocked__why">{BLOCK_ICON[info?.status ?? ''] ?? '🔒'} {blockedReason}</span>
      </span>
    )
  }
  return (
    <button type="button" className={`lc-btn ${props.primary ? 'lc-btn--primary' : ''}`} disabled={props.busy} onClick={props.onRun}>
      {props.busy ? <Clock size={13} className="ac-spin" aria-hidden="true" /> : props.icon}
      {props.busy ? 'Enregistrement…' : props.label}
    </button>
  )
}
